/**
 * Create or edit a screensaver (name, URL, active flag, assigned stores).
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Store } from '@/shared/config/app-config';
import { Dialog } from '@/shared/ui/Dialog';
import { AssignedStoresField } from './AssignedStoresField';
import { orderStoreIds } from './logic';
import { createScreenSaver, updateScreenSaver, type Asset } from './api';

export interface ScreenSaverDialogProps {
  item: Asset | null;
  stores: readonly Store[];
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const NAME_MAX = 100;
const URL_MAX = 300;

const isValidName = (value: string) =>
  value.length >= 1 && value.length <= NAME_MAX;
const isValidUrl = (value: string) =>
  value.length >= 1 && value.length <= URL_MAX;

export function ScreenSaverDialog({
  item,
  stores,
  onClose,
}: Readonly<ScreenSaverDialogProps>) {
  const creating = !item;
  const [name, setName] = useState(item?.name ?? '');
  const [url, setUrl] = useState(item?.url ?? '');
  const [active, setActive] = useState(item?.active ?? true);
  const [assignedStores, setAssignedStores] = useState<string[]>(
    (item?.assignedStores ?? []).filter(
      (id): id is string => typeof id === 'string',
    ),
  );
  const [submitted, setSubmitted] = useState(false);
  // Red border once a field has been edited and is invalid.
  const [dirty, setDirty] = useState({ name: false, url: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const model = {
        name,
        url,
        active,
        assignedStores: orderStoreIds(assignedStores, stores),
      };
      return item
        ? updateScreenSaver(item.id, model)
        : createScreenSaver(model);
    },
    onSuccess: () => onClose('Confirm'),
    onError: () =>
      setErrorMessage(
        creating
          ? 'Failed to create screensaver'
          : 'Failed to update screensaver',
      ),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (!isValidName(name) || !isValidUrl(url)) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      title={creating ? 'Screensaver creation' : 'Screensaver edit'}
      onClose={() => onClose('Cancel')}
      testId="settings-screensaver-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-5">
            <div className="mb-2">
              <label className="field-label" htmlFor="screensaver-name">
                Screensaver Name
              </label>
              <input
                id="screensaver-name"
                data-testid="settings-screensaver-form-name"
                className={`field ${dirty.name && !isValidName(name) ? 'invalid' : ''}`}
                name="name"
                placeholder="Screensaver Name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setDirty((prev) => ({ ...prev, name: true }));
                }}
                required
              />
              <small
                hidden={!submitted || isValidName(name)}
                data-testid="settings-screensaver-form-name-error"
              >
                Wrong screensaver name
              </small>
            </div>
            <div className="mb-2">
              <label className="field-label" htmlFor="screensaver-url">
                Screensaver URL
              </label>
              <input
                id="screensaver-url"
                data-testid="settings-screensaver-form-url"
                className={`field ${dirty.url && !isValidUrl(url) ? 'invalid' : ''}`}
                name="url"
                placeholder="Screensaver URL"
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setDirty((prev) => ({ ...prev, url: true }));
                }}
                required
              />
              <small
                hidden={!submitted || isValidUrl(url)}
                data-testid="settings-screensaver-form-url-error"
              >
                Wrong url
              </small>
            </div>
            <div className="mb-2 mt-4">
              <div className="styled-input-square styled-input-single">
                <input
                  type="checkbox"
                  name="active"
                  id="screensaver-active"
                  data-testid="settings-screensaver-form-active"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                />
                <label htmlFor="screensaver-active">Activate Screensaver</label>
              </div>
            </div>
            {errorMessage && (
              <div
                className="alert mb-0"
                role="alert"
                data-testid="settings-screensaver-form-error"
              >
                <span className="error-msg">{errorMessage}</span>
              </div>
            )}
            <div className="mt-6 flex justify-center">
              <button
                className="button button-form"
                type="submit"
                disabled={save.isPending}
                data-testid="settings-screensaver-form-save-button"
              >
                Save
              </button>
            </div>
          </div>
          <div className="col-span-7">
            <AssignedStoresField
              stores={stores}
              selectedIds={assignedStores}
              onChange={setAssignedStores}
              testId="settings-screensaver-form-assigned-stores"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}
